// PB-4 — usePowerballHistory hook.
//
// Reads past POWERBALL weeks via get_pb_history RPC.
// Returns a list of week summaries with both slip results.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";

// ─── Types ────────────────────────────────────────────────

export interface PbWeekSummary {
  pb10_realized_return?: number | null;
  pb10_won_count?: number | null;
  pb10_leg_count?: number | null;
  mega_realized_return?: number | null;
  mega_won_count?: number | null;
  mega_leg_count?: number | null;

  session_date?: string | null;
  pb10_publication_id?: string | null;
  pb10_publication_version?: number | null;
  mega_publication_id?: string | null;
  mega_publication_version?: number | null;
  week_number: number;
  season: string;
  combined_stake: number;
  deduplication_passed: boolean | null;
  published_at: string | null;
  pb10_status: string | null;
  pb10_result: string | null;
  pb10_combined_odds: number | null;
  pb10_stake: number | null;
  pb10_potential_win: number | null;
  pb10_confidence: number | null;
  mega_status: string | null;
  mega_result: string | null;
  mega_combined_odds: number | null;
  mega_stake: number | null;
  mega_potential_win: number | null;
  mega_confidence: number | null;
}

export interface PbPublicationSummary {
  publication_id: string;
  tier: string;
  session_date: string;
  version: number;
  combined_odds: number | null;
  leg_count: number;
}

export interface UsePowerballHistoryState {
  publications: readonly PbPublicationSummary[];
  weeks: readonly PbWeekSummary[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// ─── Hook ─────────────────────────────────────────────────

export function usePowerballHistory(): UsePowerballHistoryState {
  const { user } = useAuthSession();
  const [publications, setPublications] = useState<readonly PbPublicationSummary[]>([]);
  const [weeks, setWeeks] = useState<readonly PbWeekSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!user) {
      setWeeks([]);
      setPublications([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const client = getBrowserClient();
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const rpc = (fn: string) => (client.rpc as unknown as (name: string) => Promise<{
          data: Record<string, unknown> | null; error: { message: string } | null;
        }>).call(client, fn);
        const [{ data, error: rpcError }, archive] = await Promise.all([
          rpc("get_pb_history"), rpc("get_pb_publication_history"),
        ]);


        if (cancelled) return;

        if (archive.error) throw new Error(archive.error.message);
        setPublications(Array.isArray(archive.data?.publications)
          ? (archive.data.publications as PbPublicationSummary[]) : []);
        if (rpcError) {
          setError(rpcError.message);
          setLoading(false);
          return;
        }

        if (!data) {
          setWeeks([]);
          setLoading(false);
          return;
        }

        const raw = data as Record<string, unknown>;
        if (Array.isArray(raw.weeks)) {
          setWeeks(
            (raw.weeks as Record<string, unknown>[]).map((w) => ({
              session_date: typeof w.session_date === "string" ? w.session_date : null,
              pb10_publication_id: typeof w.pb10_publication_id === "string" ? w.pb10_publication_id : null,
              pb10_publication_version: typeof w.pb10_publication_version === "number" ? w.pb10_publication_version : null,
              mega_publication_id: typeof w.mega_publication_id === "string" ? w.mega_publication_id : null,
              mega_publication_version: typeof w.mega_publication_version === "number" ? w.mega_publication_version : null,
              pb10_realized_return: typeof w.pb10_realized_return === "number" ? w.pb10_realized_return : null,
              pb10_won_count: typeof w.pb10_won_count === "number" ? w.pb10_won_count : null,
              pb10_leg_count: typeof w.pb10_leg_count === "number" ? w.pb10_leg_count : null,
              mega_realized_return: typeof w.mega_realized_return === "number" ? w.mega_realized_return : null,
              mega_won_count: typeof w.mega_won_count === "number" ? w.mega_won_count : null,
              mega_leg_count: typeof w.mega_leg_count === "number" ? w.mega_leg_count : null,
              week_number: Number(w.week_number ?? 0),
              season: String(w.season ?? ""),
              combined_stake: Number(w.combined_stake ?? 0),
              deduplication_passed: typeof w.deduplication_passed === "boolean" ? w.deduplication_passed : null,
              published_at: typeof w.published_at === "string" ? w.published_at : null,
              pb10_status: typeof w.pb10_status === "string" ? w.pb10_status : null,
              pb10_result: typeof w.pb10_result === "string" ? w.pb10_result : null,
              pb10_combined_odds: typeof w.pb10_combined_odds === "number" ? w.pb10_combined_odds : null,
              pb10_stake: typeof w.pb10_stake === "number" ? w.pb10_stake : null,
              pb10_potential_win: typeof w.pb10_potential_win === "number" ? w.pb10_potential_win : null,
              pb10_confidence: typeof w.pb10_confidence === "number" ? w.pb10_confidence : null,
              mega_status: typeof w.mega_status === "string" ? w.mega_status : null,
              mega_result: typeof w.mega_result === "string" ? w.mega_result : null,
              mega_combined_odds: typeof w.mega_combined_odds === "number" ? w.mega_combined_odds : null,
              mega_stake: typeof w.mega_stake === "number" ? w.mega_stake : null,
              mega_potential_win: typeof w.mega_potential_win === "number" ? w.mega_potential_win : null,
              mega_confidence: typeof w.mega_confidence === "number" ? w.mega_confidence : null,
            })),
          );
        } else {
          setWeeks([]);
        }

        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [user, tick]);

  return useMemo(
    () => ({ weeks, publications, loading, error, refetch }),
    [weeks, publications, loading, error, refetch],
  );
}
